import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';

export default function BlueFlamePages() {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)' }}>
      {/* Header */}
      <div className="bg-[#C9A227]/90 text-center py-3">
        <p className="text-sm font-bold tracking-widest text-[#0a1628] uppercase">New from Grad Fawkes</p>
      </div>

      {/* Hero Image */}
      <div className="relative w-full aspect-[16/9] max-h-[70vh] overflow-hidden">
        <img 
          src="/publications/oumuamua/blue-flame-1.jpg" 
          alt="Oumuamua and the Blue Flame" 
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 -mt-32 relative z-10">
        <Link to="/publications" className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#d4af37] mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to Publications
        </Link>

        <h1 className="text-4xl md:text-6xl font-serif text-[#F3EAD3] tracking-wide text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          OUMUAMUA AND THE BLUE FLAME
        </h1>
        <p className="text-xl text-[#C9A227] italic text-center mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          A Second Tale of Tuwaqachi
        </p>

        <div className="space-y-6 text-[#B7C4D4] text-lg leading-relaxed">
          <p>
            If the first tale was the leaving, this one is the getting lost—and the learning that getting lost is 
            part of the map.
          </p>
          <p>
            Five travelers ride the rifts: a scout who turns darkness into a road, a tide-reader who sings the 
            tear before it forms, a coral-reader who listens to planets, a geometer who sees the lie in the light, 
            and a breaker who still thinks the universe can be solved with a hammer and a punchline.
          </p>
          <p>
            They needed a planet. They found a bazaar. They needed rest. They found the Games.
          </p>
        </div>

        <div className="my-12 border-t border-[#C9A227]/30" />

        <div className="text-center mb-12">
          <p className="text-[#C9A227] italic text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            The Compass points. The Flame sparks. The Gate has not forgotten how to listen.
          </p>
          <p className="text-[#C9A227] italic text-lg mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            No ending here—only the invitation.
          </p>
        </div>

        {/* Character Gallery */}
        <div className="grid grid-cols-2 gap-4 my-12">
          <div className="rounded-xl overflow-hidden border-2 border-[#C9A227]/30">
            <img src="/publications/oumuamua/blue-flame-1.jpg" alt="The Bazaar of Drift" className="w-full aspect-video object-cover" />
            <p className="text-[#C9A227] text-sm p-3 text-center">The Bazaar of Drift</p>
          </div>
          <div className="rounded-xl overflow-hidden border-2 border-[#C9A227]/30">
            <img src="/publications/oumuamua/star-compass-1.jpg" alt="Pip, Tide Reader" className="w-full aspect-video object-cover" />
            <p className="text-[#C9A227] text-sm p-3 text-center">Pip, Tide Reader</p>
          </div>
        </div>

        {/* Second Image */}
        <div className="my-12">
          <img 
            src="/publications/oumuamua/blue-flame-2.jpg" 
            alt="The Compass and the Flame" 
            className="w-full rounded-xl shadow-2xl"
          />
        </div>

        <h3 className="text-2xl text-[#C9A227] text-center mb-6 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
          The Compass and the Flame
        </h3>

        <div className="space-y-6 text-[#B7C4D4] text-lg leading-relaxed">
          <p>
            They set a course by song and slipped into the folds of space—five travelers from Tuwaqachi, 
            carrying a compass, a hammer, and the last of home.
          </p>
          <p>
            Oumuamua maps the dark by a needle of light. Pip reads the tear before it is born. Barney hears 
            the planet in the coral. Maribel counts the angles that keep left from right. And Ikulak—breaker, 
            late arrival, blue along the fin—still thinks the old joke is enough.
          </p>
          <p>
            A dark-matter ocean offers rest, then a market that smiles with too many teeth. Captives race a 
            living maze. Far from the arena, a late shark is told he is not only impact. He is supposed to be a 
            hearth.
          </p>
        </div>

        <div className="my-12 border-t border-[#C9A227]/30" />

        <div className="text-center mb-8">
          <p className="text-[#C9A227] italic text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            When Compass forgets north and Flame remembers heat, the Gate will remember how to open.
          </p>
          <p className="text-[#C9A227] italic text-lg mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Neither walks home alone.
          </p>
        </div>

        <div className="bg-[#0d1f3c]/50 rounded-xl p-6 border border-[#C9A227]/20">
          <p className="text-[#B7C4D4] text-lg leading-relaxed">
            Lyrical, fierce, and unexpectedly funny—mythic space-opera woven from Hawaiian and Inuit currents. 
            Crews who are not warriors. Maps that leak. The difference between hitting the rock and becoming the 
            room that holds your people.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-[#C9A227]/20 text-center">
          <p className="text-[#C9A227] italic mb-4">The vastfold is open. The song is already underway.</p>
          <p className="text-[#F3EAD3] font-semibold">By Grad Fawkes</p>
          
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a 
              href="https://a.co/d/07SUOsPj" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF9900] text-[#0a1628] font-semibold rounded-lg hover:bg-[#FFa500] transition-colors"
              data-testid="amazon-buy-btn"
            >
              Buy on Amazon
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61583179743030" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A227] text-[#0a1628] font-semibold rounded-lg hover:bg-[#d4af37] transition-colors"
            >
              Facebook
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.instagram.com/cocoalife.min/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#C9A227] text-[#C9A227] font-semibold rounded-lg hover:bg-[#C9A227]/10 transition-colors"
            >
              Instagram
              <ExternalLink className="w-4 h-4" />
            </a>
            <a 
              href="https://www.tiktok.com/@taleoftuwaqachi" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#C9A227] text-[#C9A227] font-semibold rounded-lg hover:bg-[#C9A227]/10 transition-colors"
            >
              TikTok
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
